"use client";

import { ForgotPasswordShell } from "@calcom/web/modules/auth/forgot-password/components/ForgotPasswordShell";
// eslint-disable-next-line no-restricted-imports
import { debounce } from "lodash";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import type { SyntheticEvent } from "react";
import React from "react";

const AUTO_PRIMARY = "#075F78";
const AUTO_PRIMARY_DARK = "#063D56";
const AUTO_PRIMARY_SOFT = "rgba(7,95,120,0.10)";
const AUTO_PRIMARY_SHADOW = "rgba(7,95,120,0.26)";

export type PageProps = {
  csrfToken?: string;
};

export default function ForgotPassword(props: PageProps) {
  const csrfToken = "csrfToken" in props ? (props.csrfToken as string) : undefined;
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<{ message: string } | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [email, setEmail] = React.useState("");

  const handleChange = (e: SyntheticEvent) => {
    const target = e.target as typeof e.target & { value: string };
    setEmail(target.value);
  };

  const submitForgotPasswordRequest = async ({ email }: { email: string }) => {
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json);
      } else {
        setSuccess(true);
      }
      return json;
    } catch (_reason) {
      setError({ message: "Erro inesperado. Tente novamente." });
    } finally {
      setLoading(false);
    }
  };

  const debouncedHandleSubmitPasswordRequest = debounce(submitForgotPasswordRequest, 250);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    await debouncedHandleSubmitPasswordRequest({ email });
  };

  const title = success ? "E-mail enviado" : "Esqueceu sua senha?";
  const description = success
    ? `Enviamos um link de redefinição para ${email}. Verifique sua caixa de entrada e spam.`
    : "Digite seu e-mail e enviaremos um link para redefinir sua senha.";

  return (
    <ForgotPasswordShell title={title} description={description}>
      {success ? (
        <Link
          href="/auth/login"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "13px 20px",
            background: `linear-gradient(90deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`,
            color: "#ffffff",
            border: "none",
            borderRadius: "12px",
            fontSize: "15px",
            fontWeight: 600,
            textDecoration: "none",
            boxShadow: `0 10px 24px ${AUTO_PRIMARY_SHADOW}`,
          }}>
          <ArrowLeft size={16} />
          Voltar ao login
        </Link>
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <input name="csrfToken" type="hidden" defaultValue={csrfToken} hidden />

            {error && (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "10px",
                  backgroundColor: "#fef2f2",
                  border: "1px solid #fecaca",
                  marginBottom: "16px",
                }}>
                <p style={{ fontSize: "13px", color: "#dc2626" }}>{error.message}</p>
              </div>
            )}

            <div style={{ marginBottom: "20px" }}>
              <label
                htmlFor="email"
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#344054",
                  marginBottom: "6px",
                }}>
                E-mail
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                onChange={handleChange}
                placeholder="seu@email.com"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  border: "1.5px solid #e6ebf0",
                  borderRadius: "12px",
                  fontSize: "15px",
                  color: "#111827",
                  backgroundColor: "#fbfcfe",
                  outline: "none",
                  transition: "all 0.2s ease",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = AUTO_PRIMARY;
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.boxShadow = `0 0 0 4px ${AUTO_PRIMARY_SOFT}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#e6ebf0";
                  e.currentTarget.style.backgroundColor = "#fbfcfe";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "13px 20px",
                background: `linear-gradient(90deg, ${AUTO_PRIMARY} 0%, ${AUTO_PRIMARY_DARK} 100%)`,
                color: "#ffffff",
                border: "none",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                transition: "all 0.2s ease",
                boxShadow: `0 10px 24px ${AUTO_PRIMARY_SHADOW}`,
              }}>
              {loading ? "Enviando..." : "Enviar link de redefinição"}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Link
              href="/auth/login"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#98a2b3",
                textDecoration: "none",
              }}>
              <ArrowLeft size={14} />
              Voltar ao login
            </Link>
          </div>
        </>
      )}
    </ForgotPasswordShell>
  );
}
